export interface GuideSection {
  heading: string;
  body: string[];
}

export interface GuideFAQ {
  question: string;
  answer: string;
}

export interface Guide {
  slug: string;
  eyebrow: string;
  title: string;
  summary: string;
  metaTitle: string;
  metaDescription: string;
  relatedCategorySlug?: string;
  sections: GuideSection[];
  faq: GuideFAQ[];
}

export const GUIDES: Guide[] = [
  {
    slug: "map-pack",
    eyebrow: "Guide",
    title: "What is the Map Pack? The Local 3-Pack, explained.",
    summary:
      "The block of three Google Business Profile results above the organic links — what it is, why proximity dominates, and how AI search is changing it.",
    metaTitle: "What is the Map Pack? The Local 3-Pack, Explained for 2026",
    metaDescription:
      "A complete guide to the Google Map Pack (Local 3-Pack): how it ranks, why proximity dominates, how it differs from organic, and how AI Overviews are changing it.",
    relatedCategorySlug: "local-rankings",
    sections: [
      {
        heading: "What the Map Pack actually is",
        body: [
          "The Map Pack is the block of three Google Business Profile results — with a small map, names, star ratings, and 'Directions' / 'Website' buttons — that appears at the top of many local search queries. Google's official name is the 'Local Pack'; the SEO community usually calls it the Map Pack, 3-Pack, or (historically) the Snack Pack.",
          "It is a fundamentally different ranking system from the ten 'blue link' organic results below it. The Map Pack pulls from the Business Profile index. The organic results pull from Google's general web index. A business can dominate the Map Pack for a query and rank nowhere organically, or vice versa. They are evaluated by different signals, refreshed on different cycles, and won by different tactics.",
        ],
      },
      {
        heading: "A short history",
        body: [
          "Before 2015, Google showed a Local 7-Pack — seven business listings stacked vertically. In August 2015 it compressed to the 3-Pack we have today, dramatically increasing competition for the visible slots. Since then the surface has stayed structurally similar, with cosmetic changes (filters, attributes, justifications) and one major addition: Local Service Ads now sit above it in many service categories.",
          "Through 2024 and into 2026, AI Overviews and AI Mode have begun displacing the Map Pack on some queries, especially research-style ones ('best Italian restaurant for a date'). On transactional queries ('pizza near me', 'dentist open now') the Map Pack remains the dominant surface.",
        ],
      },
      {
        heading: "How the Map Pack ranks businesses",
        body: [
          "Google has been unusually transparent about local ranking. Its three official factors are Relevance, Distance, and Prominence. In practice, that translates to roughly these levers, in rough order of impact:",
          "Proximity — the searcher's location at query time. By far the biggest factor. A business one block closer often outranks one a mile away, all else equal. This is why ranking varies dramatically across a city; tools like Local Falcon visualize it as a heatmap.",
          "Primary category — the single category set on the Business Profile. Wrong category means the listing isn't competing for the queries the owner thinks it is. The most common avoidable mistake in local SEO.",
          "Reviews — total volume, recency, velocity, and keyword content. Strong, steady review growth correlates tightly with ranking improvements; sudden spikes risk filtering.",
          "Website signals — relevance, authority, and topical depth of the linked site. Google increasingly uses the website to settle ties and to power 'justifications' (the snippets that appear under Map Pack results).",
          "Business name keywords — historically the single most abused lever ('Joe's Plumbing Emergency 24/7 Las Vegas'). Still works, still against guidelines, still actively spam-fought.",
          "Citations and NAP consistency — a baseline trust signal rather than a primary lever today. Worth getting right; not worth obsessing over.",
        ],
      },
      {
        heading: "How the Map Pack differs from organic",
        body: [
          "Different index, different signals, different surface. A few practical consequences: there is no 'page 2' of the Map Pack — there is only the 3-Pack and the Local Finder below it. Click data behaves differently — top-of-pack listings get the lion's share of clicks but the spread is flatter than the 1-2-3 organic distribution. And the Map Pack updates faster than organic for most signals; review and category changes can move rankings within hours.",
        ],
      },
      {
        heading: "AI Overviews and the Map Pack in 2026",
        body: [
          "AI Overviews and AI Mode are reshuffling local SERPs. On research and comparison queries, the AI summary now appears above the Map Pack — and sometimes replaces it entirely. The summaries pull from both web content and Business Profile data, which means the optimization surface has expanded: getting cited in an AI Overview requires schema, clear content structure, and a Business Profile that the AI can use as a source.",
          "Ask Maps — Google's conversational layer inside Google Maps — is the other new surface. Early data suggests it favors businesses with rich, accurate profiles (attributes, photos, recent reviews, complete services) over those with thin listings. The fundamentals haven't changed; the rewards for excellence have grown.",
        ],
      },
    ],
    faq: [
      {
        question: "What's the difference between the Map Pack and the Local 3-Pack?",
        answer: "Nothing — they're the same thing. Google calls it the Local Pack. The SEO community uses Map Pack, 3-Pack, and historically Snack Pack interchangeably.",
      },
      {
        question: "How do I get into the Map Pack?",
        answer: "Set the correct primary category, earn reviews steadily over time, optimize the linked website for the same topic, and resolve any guideline issues (especially address and business name). Proximity is the largest factor and is partly out of your control — but doing everything else well widens the radius from which you can rank.",
      },
      {
        question: "Has AI Mode killed the Map Pack?",
        answer: "No. AI Overviews appear above the Map Pack on many queries and reduce its visibility, but transactional 'near me' queries — the ones that drive phone calls — still surface the Map Pack prominently. AI search has added a new optimization layer, not removed the old one.",
      },
      {
        question: "Why does my Map Pack ranking vary by neighborhood?",
        answer: "Because proximity is the dominant factor. A geo-grid tool like Local Falcon or BrightLocal Local Search Grid shows exactly how your ranking changes across a grid of locations — the foundational diagnostic for any local SEO engagement.",
      },
    ],
  },
  {
    slug: "google-business-profile",
    eyebrow: "Guide",
    title: "Google Business Profile, explained.",
    summary:
      "What GBP is, how it differs from a website, the optimization fundamentals that actually move rankings, and the suspensions that derail listings.",
    metaTitle: "Google Business Profile, Explained — A 2026 Guide for Local Businesses",
    metaDescription:
      "Google Business Profile (formerly Google My Business): what it is, how to optimize it, common suspension causes, and how to handle them — for agencies and owners.",
    relatedCategorySlug: "google-business-profile",
    sections: [
      {
        heading: "What Google Business Profile is",
        body: [
          "Google Business Profile (GBP) is the free Google product that powers a business's appearance in Google Maps, the Map Pack, and the knowledge panel on Google Search. It was renamed from Google My Business in November 2021. The Business Profile Manager dashboard was largely retired in 2024 — most management now happens directly from the search experience, by searching the business name while signed in as an owner.",
          "A Business Profile is not a website, even though it can have a 'website' button. It's a structured listing in Google's local index, made up of fields (name, category, address, hours, services, attributes) plus user-generated content (reviews, photos, Q&A). For many local businesses — restaurants, clinics, contractors — it drives more leads than the website itself.",
        ],
      },
      {
        heading: "The fields that matter",
        body: [
          "Primary category. The most important single field on the entire profile. Pick the most specific category that genuinely describes the business. 'Pizza restaurant' beats 'Restaurant'. 'Personal injury attorney' beats 'Attorney'. If the wrong primary category is set, no other optimization will save the listing.",
          "Secondary categories. Add the categories the business also serves, but don't dilute — Google weights the primary heavily and treats long category lists as noise.",
          "Name. Use the legal or DBA name. Adding keywords or location ('Joe's Plumbing - 24/7 Emergency Las Vegas') is a guideline violation, actively fought, and a common cause of suspension.",
          "Services and Products. Underused. Each service is a separate entity Google can match to queries and use in justifications.",
          "Attributes. Wheelchair accessible, outdoor seating, free Wi-Fi, women-owned — these increasingly drive filtering and justifications.",
          "Photos. Original photos matter; stock photos do not. Profiles with steady, recent photo uploads rank better than those without.",
          "Hours, including special hours. Outdated hours kill trust and reviews.",
        ],
      },
      {
        heading: "Reviews — what works",
        body: [
          "Steady volume beats sudden volume. A business gaining 5 reviews a month for two years outperforms a business that gained 60 reviews last month. Velocity spikes draw spam filters.",
          "Respond to every review — positive and negative. The response is itself indexed content and a public signal of how the business operates.",
          "Don't gate reviews ('email us if you had a bad experience, leave a Google review if you had a good one'). It's against Google's policy and the platforms are getting better at detecting it.",
        ],
      },
      {
        heading: "Suspensions",
        body: [
          "Suspensions come in two flavors. A soft suspension removes the owner's ability to edit the listing; the listing stays visible. A hard suspension removes the listing from Maps entirely. Both are reinstatable through the Business Redressal Complaint Form, but reinstatement requires documentation — a utility bill at the address, business registration, photos of signage.",
          "The most common causes: keyword stuffing in the business name, using a virtual office or PO box as a storefront address, listing service-area businesses with a public address they don't operate from, and aggressive category changes from competitors via 'Suggest an edit'.",
          "Reinstatement timelines have stretched in 2025–2026. Two to four weeks is typical; complex cases take months. The single best protection is running a clean listing from the start.",
        ],
      },
    ],
    faq: [
      {
        question: "Is Google Business Profile the same as Google My Business?",
        answer: "Yes — Google renamed Google My Business to Google Business Profile in November 2021. The product is the same; the management surface is now mostly inside Google Search rather than a separate dashboard.",
      },
      {
        question: "Does my Business Profile rank my website too?",
        answer: "No, but it's tightly coupled. The Map Pack ranks the Business Profile, not the website — but website signals (relevance, authority, structured data) are inputs Google uses to evaluate the profile, especially for tiebreakers and justifications.",
      },
      {
        question: "How long does a GBP suspension take to fix?",
        answer: "Two to four weeks for clear-cut cases via the Business Redressal Complaint Form, often longer for complex situations or repeat suspensions. Documentation — proof of address, registration, signage — is the single biggest factor in reinstatement speed.",
      },
    ],
  },
  {
    slug: "local-seo",
    eyebrow: "Pillar",
    title: "Local SEO in 2026.",
    summary:
      "A working overview of local SEO — Map Pack, GBP, reviews, citations, on-site signals, and the AI overlay that's reshaping the entire stack.",
    metaTitle: "Local SEO in 2026 — A Working Overview for Agencies & Operators",
    metaDescription:
      "A 2026 pillar guide to local SEO: Map Pack ranking, Google Business Profile, reviews, citations, on-site signals, and how AI search is changing the playbook.",
    relatedCategorySlug: "local-rankings",
    sections: [
      {
        heading: "The two surfaces",
        body: [
          "Local SEO operates across two distinct surfaces: the Map Pack (powered by Google Business Profile) and the organic web results below it. They share signals but rank independently. A complete local SEO program optimizes both — and increasingly a third surface, AI Overviews and AI Mode, which pulls from both.",
        ],
      },
      {
        heading: "The five things that actually move local rankings",
        body: [
          "Primary category on the Business Profile. The single highest-leverage decision in local SEO.",
          "Reviews — steady volume, recent activity, content that mentions the services being searched.",
          "Proximity to the searcher. Largely outside your control, but expanded by everything else done well.",
          "On-site relevance and authority. The website's topical depth, internal linking, schema, and link profile all feed into both organic ranking and Business Profile tiebreakers.",
          "Citation accuracy and consistency. A baseline trust signal — get it right once, then stop worrying about it.",
        ],
      },
      {
        heading: "The AI overlay",
        body: [
          "AI Overviews now appear above the Map Pack on many queries. AI Mode is a parallel conversational surface. Ask Maps is the equivalent inside Google Maps. None have replaced traditional local SEO — but all three reward businesses that have invested in structured data, clear content, and rich, accurate Business Profiles. The work changes; the fundamentals don't.",
        ],
      },
      {
        heading: "Where to start",
        body: [
          "Audit the Business Profile primary category and resolve any guideline issues. Stand up a review-generation cadence the business can sustain. Make the website topical and fast. Geo-grid the rankings to understand where you actually win and lose. Read Map Observer on Fridays.",
        ],
      },
    ],
    faq: [
      {
        question: "Is local SEO different from regular SEO?",
        answer: "Yes. Local SEO targets the Map Pack (powered by Google Business Profile) and geo-modified organic queries. Traditional SEO targets the broader organic results. The signal sets overlap but the ranking systems are separate.",
      },
      {
        question: "How long does local SEO take to work?",
        answer: "Map Pack changes from category corrections or guideline fixes can show in days. Review and content-driven gains take three to six months. Recovery from a suspension is on Google's timeline, not yours.",
      },
    ],
  },
  {
    slug: "ai-search-local",
    eyebrow: "Guide",
    title: "AI Overviews, AI Mode & Ask Maps for local businesses.",
    summary:
      "How Google's AI search surfaces are changing local results — and what local SEO agencies and operators should actually do about it.",
    metaTitle: "AI Overviews, AI Mode & Ask Maps for Local SEO — A 2026 Guide",
    metaDescription:
      "How Google's AI search — AI Overviews, AI Mode, Ask Maps — is changing local results and what SEO agencies and local businesses should do about it.",
    relatedCategorySlug: "ai-search",
    sections: [
      {
        heading: "Three new surfaces, briefly",
        body: [
          "AI Overviews are the generative summaries that appear above Google's traditional results on many queries. They pull from indexed web pages and increasingly from Business Profile data.",
          "AI Mode is Google's full conversational search experience — a chat surface that handles multi-turn questions and can synthesize across many sources. For local queries, it sometimes returns Business Profile cards inline and sometimes returns prose with citations.",
          "Ask Maps is the conversational layer inside Google Maps itself. Ask a question like 'patio brunch in Eastside that takes dogs' and Maps returns a curated list with reasoning. The data source is the Business Profile graph plus reviews.",
        ],
      },
      {
        heading: "What changes for local SEO",
        body: [
          "The optimization surface has expanded but the fundamentals haven't. AI surfaces reward businesses with rich, accurate Business Profiles (attributes, services, photos, recent reviews) and websites with clear, structured, factual content. They penalize thin, generic, or contradictory information across surfaces.",
          "Citations from AI Overviews and AI Mode drive measurable traffic for content sites; for local businesses, the equivalent is being included in the AI's answer set. That requires Business Profile completeness, on-site content depth on the topic, and structured data (LocalBusiness, Service, FAQ, Review) on the relevant pages.",
        ],
      },
      {
        heading: "What to actually do",
        body: [
          "Complete every Business Profile field — services, attributes, products, photos, hours, Q&A. The marginal field that seems unimportant is precisely what AI Mode and Ask Maps use to filter.",
          "Make the website's local content explicit. Service pages, location pages, FAQ blocks, and Schema.org markup that mirror the structure of the questions users ask.",
          "Audit review content for the language customers actually use. AI surfaces lean heavily on review text for justifications and filtering.",
          "Track AI Overview presence and citation rate for your priority queries — separately from traditional rank tracking. The picture only gets clearer once you have a baseline.",
        ],
      },
    ],
    faq: [
      {
        question: "Are AI Overviews replacing the Map Pack?",
        answer: "On research and comparison queries, yes — they often appear above the Map Pack and reduce its click share. On transactional 'near me' queries, the Map Pack still dominates. Optimize for both.",
      },
      {
        question: "How do I show up in AI Overviews for local queries?",
        answer: "Complete the Business Profile thoroughly, build genuine review content, add structured data and clear topical content to the website, and earn legitimate mentions on sources the AI trusts (industry publications, local news, well-known directories).",
      },
      {
        question: "Is Ask Maps worth optimizing for in 2026?",
        answer: "It's early. But the signals it uses — profile completeness, attributes, review content — are the same signals that strengthen Map Pack ranking. There is no separate Ask Maps playbook today; doing the fundamentals well covers both.",
      },
    ],
  },
];

export function getGuide(slug: string): Guide | undefined {
  return GUIDES.find((g) => g.slug === slug);
}
